import * as React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { LoadingIndicator } from '../LoadingIndicator';
import { ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentTextStyle } from '../styles';
import { COLORS, focusStyles, invertedFocusStyles } from '../styles';
import { Size, Testable } from '../types';
import { defaultOnMouseDownHandler } from '../common';

const getBorderRadius = (flatEdge: string | undefined, radius: number) => {
  switch (flatEdge) {
    case 'left':
      return `0px ${radius}px ${radius}px 0px`;

    case 'right':
      return `${radius}px 0px 0px ${radius}px`;

    case 'none':
    default:
      return `${radius}px`;
  }
};

export const TextButtonContentContainer = styled.div`
  min-width: 64px;
`;

const TextButtonBaseStyled = styled.button<{$iconOnly: boolean; $size: Size; $flatEdge: string | undefined; $width: string; $invertFocus: boolean | undefined; }>``;

export const TextButtonPrimaryStyled = styled(TextButtonBaseStyled)`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  position: relative;
  box-sizing: border-box;  
  &.loading-state {
    cursor: wait;
  }
  ${TextButtonContentContainer} {
    ${(props) =>
      props.$size === Size.Large
        ? ComponentLStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme))
        : props.$size === Size.Small
        ? ComponentSStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme))
        : ComponentMStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme))};

    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-fill', defaultVariant: 'primary' }, props.theme)};
    border-radius: ${(props) => getBorderRadius(props.$flatEdge, 8)};

    width: ${(props) => props.$width};
    position: relative;

    min-height: ${(props) => (props.$size === Size.Large ? '56px' : props.$size === Size.Small ? '32px' : '40px')};

    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${(props) => (props.$size === Size.Large ? '14px 16px' : props.$size === Size.Small ? '6px 8px' : '8px 12px')};

    span.icon:not(.loading) {
      pointer-events: none;
    }

    span.loading {
      opacity: 0;
    }

    .icon {
      margin: ${(props) => (props.$size === Size.Medium ? props.$iconOnly ? '-2px -8px' : '' : (props.$size === Size.Small ? props.$iconOnly ? '-2px -6px' : '' : '' ))};
      width: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')};
      height: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')};
      svg {
        width: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')} !important;
        height: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')} !important;
      }
    }

    .label {
      margin: 0 4px;
    }

    div {
      position: absolute;
      width: 100%;
      svg {
        height: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')};
        width: ${(props) => (props.$size === Size.Large ? '28px' : props.$size === Size.Small ? '20px' : '24px')};
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'icon', isOnFill: true, defaultVariant: 'primary' }, props.theme)};
        }
      }
    }
  }
  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-fill', state: 'hover', defaultVariant: 'primary' }, props.theme)};
    color: ${(props) => COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme)};
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-fill', state: 'active', defaultVariant: 'primary' }, props.theme)};
    color: ${(props) => COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme)};
  }

  &:focus,
  &.focus-state {
    ${(props) => (props.$invertFocus || (props.$invertFocus === undefined) ? invertedFocusStyles : focusStyles)}
  }
  &:disabled,
  &.disabled-state {
    cursor: not-allowed;
  }
  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    pointer-events: none;
    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-fill', state: 'disabled' }, props.theme)};
    color: ${(props) => COLORS.generateToken({ componentType: 'text', isOnFill: true, state: 'disabled' }, props.theme)};
  }
`;

export const TextButtonSecondaryStyled = styled(TextButtonPrimaryStyled)`
  ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};
    background-color: transparent;
    div svg path {
      fill: ${(props) => COLORS.generateToken({ componentType: 'icon', defaultVariant: 'primary' }, props.theme)};
    }
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', defaultVariant: 'primary' }, props.theme)};
    
    padding: ${(props) => (props.$size === Size.Large ? '12px 16px' : props.$size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'primary' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'hover', defaultVariant: 'primary' }, props.theme)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'primary' }, props.theme)};
        }
      }
    }
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'active', defaultVariant: 'primary' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'active', defaultVariant: 'primary' }, props.theme)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'primary' }, props.theme)};
        }
      }
    }
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: transparent;
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'disabled' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'disabled' }, props.theme)};
  }
`;


export const TextButtonInvertedSecondaryStyled = styled(TextButtonPrimaryStyled)`
  ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'inverse' }, props.theme)};
    background-color: transparent;
    div svg path {
      fill: ${(props) => COLORS.generateToken({ componentType: 'icon', defaultVariant: 'inverse' }, props.theme)};
    }
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', defaultVariant: 'inverse' }, props.theme)};
    
    padding: ${(props) => (props.$size === Size.Large ? '12px 16px' : props.$size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'inverse' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'hover', defaultVariant: 'inverse' }, props.theme)};

    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'inverse' }, props.theme)};
        }
      }
    }
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'active', defaultVariant: 'inverse' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'active', defaultVariant: 'inverse' }, props.theme)};

    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'inverse' }, props.theme)};
        }
      }
    }
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: transparent;
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'disabled', defaultVariant: 'inverse' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'disabled', defaultVariant: 'inverse' }, props.theme)};
  }
`;

export const TextButtonTertiaryStyled = styled(TextButtonPrimaryStyled)`
${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};
    background-color: transparent;

    div svg path {
      fill: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};
    }

    padding: ${(props) => (props.$size === Size.Large ? '12px 16px' : props.$size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-surface', state:'hover' }, props.theme)};
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};

    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};
        }
      }
    }
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    background-color: ${(props) => COLORS.generateToken({ componentType: 'bg-surface', state: 'active' }, props.theme)};
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};

    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'primary' }, props.theme)};
        }
      }
    }
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: transparent !important;
    color: ${props => COLORS.generateToken({ componentType: 'text', isOnFill:true, state:'disabled' }, props.theme)};
    border-color: transparent !important;
  }
`;

export const TextButtonPositiveStyled = styled(TextButtonPrimaryStyled)`
  ${TextButtonContentContainer} {
    color: white;
    background-color: ${props => COLORS.getColor('positive_500', props.theme)};
  }
  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.getColor('positive_700', props.theme)};
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.getColor('positive_800', props.theme)};
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.getColor('neutral_100', props.theme)};
    color: ${props => COLORS.getColor('neutral_300', props.theme)};
  }
`;

export const TextButtonDestructiveStyled = styled(TextButtonPrimaryStyled)`
${TextButtonContentContainer} {
    color: white;
    background-color: ${props => COLORS.generateToken({ componentType: 'bg-fill', defaultVariant: 'critical' }, props.theme)};
  }
  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.generateToken({ componentType: 'bg-fill', state:'hover', defaultVariant: 'critical' }, props.theme)};
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.generateToken({ componentType: 'bg-fill', state:'active', defaultVariant: 'critical' }, props.theme)};
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: ${props => COLORS.generateToken({ componentType: 'bg-fill', state: 'disabled' }, props.theme)};
    color: ${props => COLORS.generateToken({ componentType: 'text', isOnFill: true, state: 'disabled' }, props.theme)};    
  }
`;

export const TextButtonSecondaryDestructiveStyled = styled(TextButtonPrimaryStyled)`
  ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', defaultVariant: 'critical' }, props.theme)};
    background-color: transparent;
    div svg path {
      fill: ${(props) => COLORS.generateToken({ componentType: 'icon', defaultVariant: 'critical' }, props.theme)};
    }
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', defaultVariant: 'critical' }, props.theme)};
    
    padding: ${(props) => (props.$size === Size.Large ? '12px 16px' : props.$size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > ${TextButtonContentContainer},
  &.hover-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'critical' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'hover', defaultVariant: 'critical' }, props.theme)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'critical' }, props.theme)};
        }
      }
    }
  }
  &:active > ${TextButtonContentContainer},
  &.active-state > ${TextButtonContentContainer} {
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'active', defaultVariant: 'critical' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'active', defaultVariant: 'critical' }, props.theme)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => COLORS.generateToken({ componentType: 'text', state:'hover', defaultVariant: 'critical' }, props.theme)};
        }
      }
    }
  }

  &:disabled > ${TextButtonContentContainer},
  &.disabled-state > ${TextButtonContentContainer} {
    background-color: transparent;
    color: ${(props) => COLORS.generateToken({ componentType: 'text', state:'disabled' }, props.theme)};
    box-shadow: inset 0 0 0 2px ${(props) => COLORS.generateToken({ componentType: 'border', state:'disabled' }, props.theme)};
  }
`;


export interface TextButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Look and feel variant of the component. Defaults to 'primary'. */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'positive' | 'destructive' | 'secondaryDestructive' | 'secondaryInverse';
  /** Can be used in order to remove rounded corners on right or left edge of the button. Used by 'DualFunctionButton'. Defaults to 'undefined' */
  flatEdge?: 'right' | 'left' | 'none' | undefined;
  /** Size of the component. Small, Medium or Large. Defaults to 'medium' */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Allows using inverted colors for outlining focus rect. Shown when component focused by keyboard. */
  invertFocus?: boolean;
  /** Can be used to set custom value for width style. Value is set for 'ButtonContent' container. */
  width?: 'auto' | string;
  /** If set, then 'opacity' of children container will be set to 0, and loading indicator will be shown.  */
  loading?: boolean;
  /** TestId. Will be set to the top level button component. */
  testId?: string;
  /** Icon that will be shown on the left of the children. */
  icon?: React.ReactNode;
  /** Icon position in the button. Defaults to 'left' */
  iconPosition?: 'left' | 'right';
}

/**
 * TextButton component
 */
const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  ({ children = '', variant = 'primary', type = 'button', size = Size.Medium, invertFocus,  width: width = 'auto', testId, disabled, flatEdge, icon, iconPosition = 'left', ...props }, ref) => {
    // Let's filter out properties that we don't need to render.
    const { loading, ...renderProps } = props;

    const theme = useTheme();
    const renderIcon = () => (
      <><span className={(children ? 'extramargin ' : '') + (loading ? 'icon loading' : 'icon')}>{icon}</span></>
    )

    const renderContent = () => (
      <>
        {icon && iconPosition == 'left' && renderIcon()}
        {children && <span className={loading ? 'label loading' : 'label'}>{children}</span>}
        {icon && iconPosition == 'right' && renderIcon()}
        {loading && <LoadingIndicator />}
      </>
    );

    let TextButtonStyled = TextButtonPrimaryStyled;
    switch (variant) {
      case 'secondary':
        TextButtonStyled = TextButtonSecondaryStyled;
        break;
      case 'tertiary':
        TextButtonStyled = TextButtonTertiaryStyled;
        break;
      case 'positive':
        TextButtonStyled = TextButtonPositiveStyled;
        break;
      case 'destructive':
        TextButtonStyled = TextButtonDestructiveStyled;
        break;
      case 'secondaryDestructive':
        TextButtonStyled = TextButtonSecondaryDestructiveStyled;
        break;
      case 'secondaryInverse':
        TextButtonStyled = TextButtonInvertedSecondaryStyled;
        break;
    }

    return (
      <TextButtonStyled
        {...renderProps}
        $size={size}
        $invertFocus={invertFocus}        
        ref={ref}
        disabled={disabled}
        $iconOnly={!children}
        type={type}
        $flatEdge={flatEdge}
        $width={width}
        data-testid={testId}
        className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}
        onMouseDown={defaultOnMouseDownHandler}>
        <TextButtonContentContainer>{renderContent()}</TextButtonContentContainer>
      </TextButtonStyled>
    );
  },
);

export default TextButton;
